import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { SharedAccess, UserAccess, UserModelRole } from "target_wasm";
import { SearchBar } from "components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { TabToggle } from "components/common/TabToggle";
import { Button } from "components/common/Button";
import { flushSync } from "react-dom";
import { SpaceDotLabel } from "@/components/main/spaces/SpaceDot";
import { HINT_TEXT } from "@/components/main/overview/ListEmptyShell";
import { OpeningSpinner } from "@/components/main/overview/OpeningSpinner";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { fadeIn, glide, tweenHeight } from "@/lib/motion";
import { UserAccountResponse } from "components/entities";
import { fetchAccountDetails } from "components/main/utils/models/ModelServices";
import { EmailSection } from "./EmailSection";
import { AccessList } from "./AccessList";
import {
  AccessItem,
  LoadFailed,
  LoadingRows,
  PeopleScroll,
  SHOWN_ROWS,
  SpaceBlock,
} from "./AccessItem";
import { StaticRole } from "./PermissionControl";
import {
  DirectRole,
  Permission,
  RefetchOptions,
  RemoveAccessData,
  ResourceType,
  ShareOutcome,
} from "./entities";
import { SpaceAccess, SpacePerson } from "./useModelSpaceAccess";

// Side by side, a group shows this many rows; Show all gives one group the zone.
const ROWS = 4;
const SEARCH_PAST = 5;
// The server's largest page (`MAX_PAGE_SIZE`), so a search's direct matches
// usually arrive whole and one list can page them with the space's.
const SEARCH_PAGE = 50;
const HINT_ROW = `item-height flex items-center px-2 ${HINT_TEXT}`;

type DirectTab = "all" | DirectRole;
type SpaceTab = "all" | "owner" | "collaborator" | "viewer";
type RoleTab = Exclude<DirectTab | SpaceTab, "all">;
/// What each role's tab says, and its line when nobody holds the role.
const ROLE_TAB: Record<RoleTab, { label: string; empty: string }> = {
  owner: { label: "Owners", empty: "No owners here." },
  collaborator: { label: "Collaborators", empty: "No collaborators here." },
  viewer: { label: "Viewers", empty: "No viewers here." },
  invited: { label: "Invited", empty: "No pending invitations." },
};
const DIRECT_TABS: readonly DirectTab[] = [
  "all",
  "collaborator",
  "viewer",
  "invited",
];
// A space's people are its accounts, managers among them: Owners, never Invited.
const SPACE_TABS: readonly SpaceTab[] = [
  "all",
  "owner",
  "collaborator",
  "viewer",
];

const RoleStrip = <T extends DirectTab | SpaceTab>(props: {
  tabs: readonly T[];
  value: T;
  onChange: (next: T) => void;
}) => (
  <div className="mb-(--spacing-sm)">
    <TabToggle
      framed
      ariaLabel="Filter by role"
      value={props.value}
      onChange={props.onChange}
      options={props.tabs.map((tab) => ({
        value: tab,
        label: tab === "all" ? "All" : ROLE_TAB[tab as RoleTab].label,
      }))}
    />
  </div>
);
const EmptyTab: React.FC<{ tab: RoleTab }> = ({ tab }) => (
  <p className={HINT_ROW}>{ROLE_TAB[tab].empty}</p>
);

interface AccessPanelProps {
  entityId: string;
  resourceType: ResourceType;
  /// Above the composer, for people who can share: what sharing gives them.
  notice?: React.ReactNode;
  /// Under the composer: options of the share itself.
  shareOptions?: React.ReactNode;
  sharedWithList: SharedAccess | undefined;
  setSharedWithList?: React.Dispatch<
    React.SetStateAction<SharedAccess | undefined>
  >;
  canShare: boolean;
  shareDisabled?: boolean;
  canRevokeAccess: boolean;
  defaultRole?: UserModelRole;
  defaultPermission?: Permission;
  continuationToken?: string | null;
  /// Resolves false when the page did not arrive.
  onRefetchSharedList?: (options?: RefetchOptions) => Promise<boolean | void>;
  onShare: (
    emails: string[],
    roleOrPermission: UserModelRole | Permission | null,
  ) => Promise<ShareOutcome | void>;
  onRevokeAccess: (data: RemoveAccessData) => Promise<void>;
  onResendInvitation?: (email: string) => Promise<void>;
  /// The space's people; absent for anyone who cannot see the space.
  space?: SpaceAccess;
  /// The reader's role, for their own row when nobody else is listed.
  viewerRole?: string;
}

const SpacePersonRow: React.FC<{
  person: SpacePerson;
  roleTone: "regular" | "medium";
  emailShown?: boolean;
  source?: React.ReactNode;
}> = ({ person, roleTone, emailShown, source }) => (
  <AccessItem
    primaryText={person.name}
    isCurrentUser={person.isSelf}
    email={person.email !== person.name ? person.email : undefined}
    emailShown={emailShown}
    source={source}
    permissionLabel={<StaticRole label={person.role} tone={roleTone} />}
  />
);

const SelfRow: React.FC<{ role: string }> = ({ role }) => {
  const email = localStorage.getItem("email");
  const [details, setDetails] = useState<UserAccountResponse>();
  useEffect(() => {
    void fetchAccountDetails(setDetails);
  }, [email]);
  const name = details?.account_details?.name;
  return (
    <AccessItem
      primaryText={name || email || ""}
      isCurrentUser
      email={name && email ? email : undefined}
      permissionLabel={<StaticRole label={role} tone="medium" />}
    />
  );
};

/// One row a person. Someone holding both a Viewer and a Collaborator grant
/// has Collaborator's access, so that is the row kept, where the first stood.
const byUserOnce = (users: UserAccess[]): UserAccess[] => {
  const at = new Map<string, number>();
  const once: UserAccess[] = [];
  for (const access of users) {
    const id = access.user_js().user_id_js();
    const held = at.get(id);
    if (held === undefined) {
      at.set(id, once.length);
      once.push(access);
    } else if (access.permission_display_js() === "Collaborator") {
      once[held] = access;
    }
  }
  return once;
};

export const AccessPanel: React.FC<AccessPanelProps> = (props) => {
  const [query, setQuery] = useState("");
  // A non-personal org's roster is suggested to the composer.
  const modelOrg = useOrgContextStore((s) =>
    s.orgs.find((org) => org.org_id === props.space?.orgId),
  );
  const [isSearching, setIsSearching] = useState(false);
  const [serverFiltered, setServerFiltered] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // The group Show all gave the zone; a search shows both and returns here.
  const [expanded, setExpanded] = useState<"direct" | "space" | null>(null);
  const zone = useRef<HTMLDivElement>(null);
  const spaceSection = useRef<HTMLElement>(null);
  const directSection = useRef<HTMLElement>(null);
  const spaceListId = useId();
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestSearch = useRef(0);
  const isInitialMount = useRef(true);
  const pagedThisVisit = useRef(false);
  // The strip's tab, and the tab the rows (and their token) belong to; they
  // differ only while a switch is on its way.
  const [tab, setTab] = useState<DirectTab>("all");
  const [rowsTab, setRowsTab] = useState<DirectTab>("all");
  const switching = tab !== rowsTab;
  const latestSwitch = useRef(0);
  // While Show all is open, the list keeps All's height and the search stays,
  // so switching tabs moves nothing.
  const [hold, setHold] = useState<{ rows: number; search: boolean } | null>(
    null,
  );
  // The space's people arrive whole, so its tabs filter them here.
  const [spaceTab, setSpaceTab] = useState<SpaceTab>("all");

  // Trimmed as the server trims, or a trailing space hides its matches.
  const typed = query.trim().toLowerCase();
  // The term whose results show: a server search keeps the view until its
  // answer lands, then swaps it whole.
  const [term, setTerm] = useState("");
  const [resultsShown, setResultsShown] = useState(SHOWN_ROWS);
  const hasMore = !!props.continuationToken;
  const space = props.space;
  const spaceName = space?.name ?? "";
  const people = space?.state === "ready" ? space.people : [];
  const spaceShown =
    spaceTab === "all"
      ? people
      : people.filter((person) => person.role.toLowerCase() === spaceTab);

  const direct = useMemo(
    () => byUserOnce(props.sharedWithList?.users() ?? []),
    [props.sharedWithList],
  );
  // With nothing else listed, the space shows its people beside Direct access.
  const spaceListed = direct.length === 0 && !hasMore;

  // Read through a ref: rerunning the search effect when a page or a token
  // lands would send the same search again.
  const now = {
    hasMore,
    serverFiltered,
    rowsTab,
    refetch: props.onRefetchSharedList,
  };
  const listNow = useRef(now);
  listNow.current = now;

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    const mine = ++latestSearch.current;
    if (typed) setSpaceTab("all");
    const { hasMore, serverFiltered, rowsTab, refetch } = listNow.current;
    const show = () => {
      if (mine !== latestSearch.current) return;
      setTerm(typed);
      setResultsShown(SHOWN_ROWS);
      setIsSearching(false);
    };
    // A role tab's rows are not everyone, so a search asks the server for all.
    const askServer =
      serverFiltered || (typed && (hasMore || rowsTab !== "all"));
    if (!refetch || !askServer) return show();

    setIsSearching(true);
    if (!typed) {
      void refetch().then(() => {
        if (mine === latestSearch.current) setServerFiltered(false);
        show();
      });
      return;
    }
    searchTimeout.current = setTimeout(() => {
      // Only once the search is sent: a search cleared before then keeps the tab.
      latestSwitch.current++;
      setTab("all");
      setRowsTab("all");
      setServerFiltered(true);
      void refetch({ search: typed, limit: SEARCH_PAGE }).then(show);
    }, 300);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [typed]);

  const expand = (group: "direct" | "space" | null) => {
    // Folding Direct starts it from All's first page again; reloading it also
    // drops a page still on its way.
    if (expanded === "direct" && pagedThisVisit.current) {
      const mine = ++latestSwitch.current;
      setTab(rowsTab);
      void props.onRefetchSharedList?.().then((landed) => {
        if (mine === latestSwitch.current && landed !== false) {
          setTab("all");
          setRowsTab("all");
        }
      });
    }
    const holdAll = (rows: number) =>
      setHold({ rows: Math.min(rows, SHOWN_ROWS), search: showSearch });
    if (group === "direct") {
      setTab(rowsTab);
      holdAll(direct.length + (hasMore ? 1 : 0));
    } else if (group === "space") {
      holdAll(people.length);
    } else {
      // Folding starts the space on All again, as Direct starts on All.
      setSpaceTab("all");
      setHold(null);
    }
    pagedThisVisit.current = false;
    const directReturns = expanded === "space" && group === null;
    tweenHeight(zone.current, () =>
      glide(spaceSection.current, zone.current, () =>
        flushSync(() => setExpanded(group)),
      ),
    );
    // The space glides down over it as it comes back, so it fades in.
    if (directReturns) fadeIn(directSection.current);
  };

  const handleLoadMore = async () => {
    if (!props.continuationToken || !props.onRefetchSharedList) return;
    if (expanded === "direct") pagedThisVisit.current = true;
    setIsLoadingMore(true);
    try {
      await props.onRefetchSharedList({
        continuationToken: props.continuationToken,
        ...(term ? { search: term, limit: SEARCH_PAGE } : {}),
        ...(rowsTab !== "all" ? { role: rowsTab } : {}),
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  // The rows and their token stay until the new tab's first page lands; if it
  // fails, the strip goes back to the tab they belong to.
  const pickTab = async (next: DirectTab) => {
    if (next === tab || !props.onRefetchSharedList) return;
    const mine = ++latestSwitch.current;
    pagedThisVisit.current = true;
    setTab(next);
    const landed = await props.onRefetchSharedList(
      next === "all" ? undefined : { role: next },
    );
    if (mine !== latestSwitch.current) return;
    if (landed === false) setTab(rowsTab);
    else setRowsTab(next);
  };
  // Through the ref: a role change can land after another tab was picked.
  const reloadTab = useCallback(() => {
    const { rowsTab, refetch } = listNow.current;
    if (rowsTab !== "all") void refetch?.({ role: rowsTab });
  }, []);

  const matches = (name: string, email: string) =>
    name.toLowerCase().includes(term) || email.toLowerCase().includes(term);
  const directShown = term
    ? direct.filter((access) =>
        matches(
          access.user_js().user_name_js(),
          access.user_js().user_email_js(),
        ),
      )
    : direct;
  const directRole = new Map(
    direct.map((a) => [a.user_js().user_id_js(), a.permission_display_js()]),
  );
  const spaceIds = new Set(people.map((person) => person.id));

  const directRows = (
    users: UserAccess[],
    options: {
      emailShown?: boolean;
      sourceOf?: (userId: string) => React.ReactNode;
    } = {},
  ) => (
    <AccessList
      entityId={props.entityId}
      sharedWith={users}
      resourceType={props.resourceType}
      canRevokeAccess={props.canRevokeAccess}
      onRevokeAccess={props.onRevokeAccess}
      setSharedWithList={props.setSharedWithList}
      onResendInvitation={props.onResendInvitation}
      onRoleChanged={reloadTab}
      {...options}
    />
  );
  const roleTone = props.canRevokeAccess ? "regular" : "medium";
  // A search result reached through the space carries its dot, as a model row
  // in the overview does: the name on hover.
  const throughSpace = (also: boolean) =>
    space && (
      <SpaceDotLabel
        name={spaceName}
        color={space.color}
        srLabel={`${also ? "Also through" : "Through"} ${spaceName}`}
      />
    );
  // Someone also shared directly keeps that stronger role (D69); say so here.
  const spaceRows = (shown: SpacePerson[]) =>
    shown.map((person) => {
      const alsoDirect = directRole.get(person.id);
      return (
        <SpacePersonRow
          key={person.id}
          person={person}
          roleTone={roleTone}
          source={alsoDirect ? `${alsoDirect} directly` : undefined}
        />
      );
    });
  // While a page loads its label stays and dims, and the overview's spinner
  // opens in front of it, easing the label over rather than jumping it.
  const loadMoreRow = (onClick: () => void) => (
    <div className="item-height flex items-center justify-center px-2">
      <span
        className={`flex shrink-0 overflow-hidden transition-[width,margin-right] duration-(--duration-short) ease-(--ease-move) ${isLoadingMore ? "w-4 mr-1.5" : "w-0"}`}
        aria-hidden
      >
        {isLoadingMore && <OpeningSpinner color="var(--color-fg-muted)" />}
      </span>
      <span
        className={`transition-opacity duration-(--duration-short) ${isLoadingMore ? "opacity-50" : ""}`}
      >
        <Button
          variant="link"
          size="xs"
          className="section-rule-action"
          aria-disabled={isLoadingMore || undefined}
          onClick={isLoadingMore ? undefined : onClick}
        >
          Load more
        </Button>
      </span>
      {isLoadingMore && (
        <span className="sr-only" role="status">
          Loading more people…
        </span>
      )}
    </div>
  );
  // Held back while a search or a tab is on its way: a page asked for now would
  // be the newest request, and their answer would be dropped as stale.
  const loadMore =
    hasMore && !isSearching && !switching && loadMoreRow(handleLoadMore);
  const noMatch = <p className={HINT_ROW}>No one matches “{term}”.</p>;

  const directTitle = "Direct access";
  const showAll = (
    group: "direct" | "space",
    shown: boolean,
    controls?: string,
  ) => ({
    label: shown ? "Show less" : "Show all",
    onClick: () => expand(shown ? null : group),
    expanded: shown,
    controls,
  });

  const spaceBlock = (shown: boolean) => {
    if (!space) return null;
    const ready = space.state === "ready";
    const open = !ready || shown || spaceListed;
    const toggles = ready && !(!shown && spaceListed && people.length <= ROWS);
    return (
      // Opaque, so it covers Direct access as it glides back down over it.
      <section
        ref={spaceSection}
        aria-label="Space access"
        className="relative bg-bg-subtle"
      >
        <SectionRule
          label="Space access"
          action={toggles ? showAll("space", shown, spaceListId) : undefined}
        />
        <SpaceBlock
          name={spaceName}
          color={space.color}
          caption="Managed in Space Settings"
          listId={spaceListId}
          open={open}
          count={ready ? people.length : undefined}
        >
          {space.state === "loading" ? (
            <LoadingRows label={`Loading the people in ${spaceName}`} />
          ) : space.state === "failed" ? (
            <LoadFailed onRetry={space.retry}>
              Couldn’t load the people in {spaceName}.
            </LoadFailed>
          ) : (
            <>
              {shown && (
                <RoleStrip
                  tabs={SPACE_TABS}
                  value={spaceTab}
                  onChange={setSpaceTab}
                />
              )}
              <PeopleScroll
                rows={shown ? SHOWN_ROWS : ROWS}
                minRows={shown ? hold?.rows : undefined}
              >
                {spaceShown.length === 0 && spaceTab !== "all" ? (
                  <EmptyTab tab={spaceTab} />
                ) : (
                  <div className="space-y-1">{spaceRows(spaceShown)}</div>
                )}
              </PeopleScroll>
            </>
          )}
        </SpaceBlock>
      </section>
    );
  };

  const directGroup = (shown: boolean) => (
    <section ref={directSection} aria-label={directTitle}>
      <SectionRule
        label={directTitle}
        action={
          shown || direct.length > ROWS || hasMore
            ? showAll("direct", shown)
            : undefined
        }
      />
      {shown ? (
        <>
          {props.resourceType === ResourceType.Model && (
            <RoleStrip
              tabs={DIRECT_TABS}
              value={tab}
              onChange={(next) => void pickTab(next)}
            />
          )}
          <PeopleScroll minRows={hold?.rows}>
            {rowsTab !== "all" && direct.length === 0 && !hasMore ? (
              <EmptyTab tab={rowsTab} />
            ) : (
              directRows(direct)
            )}
            {loadMore}
          </PeopleScroll>
        </>
      ) : (
        directRows(direct.slice(0, ROWS))
      )}
    </section>
  );

  const body = (): React.ReactNode => {
    // Not for an empty role tab, which keeps its strip to get back to All.
    const nobody = direct.length === 0 && !hasMore && rowsTab === "all";
    if (props.viewerRole && !space && nobody && !term)
      return <SelfRow role={props.viewerRole} />;

    // One list, each row saying where its access comes from; someone with
    // both kinds appears once, as their direct row.
    if (term) {
      const spaceOnly = people.filter(
        (person) =>
          !directRole.has(person.id) && matches(person.name, person.email),
      );
      const found = directShown.length + spaceOnly.length;
      // A server search can answer an empty page and still hold more.
      if (found === 0)
        return hasMore ? (
          <>
            <p className={HINT_ROW}>No one matches “{term}” yet.</p>
            {loadMore}
          </>
        ) : (
          noMatch
        );
      // Paged as one list; the server is asked only once all it sent shows. shortcut:
      // past SEARCH_PAGE direct matches a page lands above the space's; upgrade then.
      const directPart = directShown.slice(0, resultsShown);
      const spacePart = spaceOnly.slice(0, resultsShown - directPart.length);
      const showMore = () => {
        setResultsShown((n) => n + SHOWN_ROWS);
        if (found <= resultsShown) void handleLoadMore();
      };
      return (
        <PeopleScroll>
          {directRows(directPart, {
            emailShown: true,
            sourceOf: (id) => spaceIds.has(id) && throughSpace(true),
          })}
          <div className="space-y-1 mt-1">
            {spacePart.map((person) => (
              <SpacePersonRow
                key={person.id}
                person={person}
                roleTone={roleTone}
                emailShown
                source={throughSpace(false)}
              />
            ))}
          </div>
          {(found > resultsShown || hasMore) &&
            !isSearching &&
            loadMoreRow(showMore)}
        </PeopleScroll>
      );
    }

    // Each group keeps its slot, so the toggle pressed stays mounted and focused.
    // Direct access stays while more pages wait, even before a row arrives, and
    // an empty role tab keeps Show all.
    const directListed = direct.length > 0 || hasMore;
    const only =
      expanded === "direct" && (directListed || rowsTab !== "all")
        ? "direct"
        : expanded === "space" && space
          ? "space"
          : null;
    return (
      <div className="flex flex-col gap-(--spacing-md)">
        {only !== "space" &&
          (directListed || only === "direct"
            ? directGroup(only === "direct")
            : (props.canShare || !space) && (
                <section aria-label={directTitle}>
                  <SectionRule label={directTitle} />
                  <p className={HINT_ROW}>
                    {props.canShare
                      ? "No direct shares yet. People you add above show up here."
                      : "Not shared with anyone yet."}
                  </p>
                </section>
              ))}
        {only !== "direct" && spaceBlock(only === "space")}
      </div>
    );
  };

  // Past five people, while more pages wait, or while a search's results show,
  // since a cleared server search keeps them until the full list lands.
  const showSearch =
    !!hold?.search ||
    direct.length + people.length > SEARCH_PAST ||
    hasMore ||
    !!query ||
    !!term;

  return (
    <div className="flex flex-col">
      {props.canShare && (
        <>
          <div className="flex flex-col gap-(--spacing-sm)">
            {props.notice}
            <EmailSection
              entityId={props.entityId}
              shareAction={props.onShare}
              resourceType={props.resourceType}
              defaultRole={props.defaultRole ?? null}
              defaultPermission={props.defaultPermission ?? null}
              shareDisabled={props.shareDisabled}
              rosterOrg={
                modelOrg && !modelOrg.is_personal
                  ? { id: modelOrg.org_id, name: modelOrg.name }
                  : undefined
              }
            />
            {props.shareOptions}
          </div>
          {/* With a search field below, a rule marks where the list begins. */}
          {showSearch ? (
            <hr className="my-(--spacing-lg) border-border" />
          ) : (
            <div className="h-(--spacing-lg)" />
          )}
        </>
      )}

      <div className="flex flex-col gap-(--spacing-md)">
        {showSearch && (
          <SearchBar
            value={query}
            onChange={setQuery}
            placeholder="Search people"
            isLoading={isSearching}
            // Marked handled, so the dialog's Escape clears the search first.
            onKeyDown={(e) => {
              if (e.key !== "Escape" || !query) return;
              e.preventDefault();
              setQuery("");
            }}
          />
        )}
        <div ref={zone}>{body()}</div>
      </div>
    </div>
  );
};
